import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import Link from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    orderDetailsPageLink:getPageLinkByPageType(state, "OrderDetailsPage"),
});

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    shipTo?: GridItemProps;
    channel?: GridItemProps;
    customerPo?: GridItemProps;
    orderDateLabel?: InjectableCss;
    orderDateValue?: InjectableCss;
    orderDateWrapper?: InjectableCss;
    orderGrandTotalDisplay?: InjectableCss;
    orderView?: InjectableCss;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderSummaryCardGridItem: {
        width: 12,
        css: css`
            padding: 0;
            flex-direction: column;
        `,
    },
    container: { 
        gap: 5,
        css: css`
            background-color: #fff;
            padding: .688rem .938rem;
            box-shadow: 0 2px .75rem 0 rgba(0,0,0,0.04);
            justify-content: space-around;
            text-align: justify;
            position: relative;
        
            p[data-test-selector="heading"] {
                color: #005CB9;
                font-family: 'Roboto', sans-serif;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .81px;
                line-height: 1rem;
                white-space: nowrap;
                text-transform: none;
            }

            p[data-test-selector="text"] {
                color: #2E384D;
                font-family: 'Lato',sans-serif;
                font-size: .875rem;
                letter-spacing: .5px;
                line-height: 1.375rem;
                white-space: normal;
                word-break: break-word;
            }

            > div {
                justify-content: flex-start;
                text-align: justify;
            }

            @media screen and (min-width: 768px) and (max-width: 1024px) {
                p[data-test-selector="heading"] {
                    font-size: .813rem;
                    letter-spacing: .5px;
                }
    
                p[data-test-selector="text"] {
                    font-size: .813rem;
                    letter-spacing: .2px;
                }
            }

            @media screen and (min-width: 1024px) {
                padding: 0.688rem 1.938rem;
            }

            @media screen and (max-width: 926px) {
                > div {
                    .orderSummaryRowData {
                        > div {
                            width: 50%;
                        }
                    }
                }

                p[data-test-selector="heading"] {
                    display: inline-flex;
                    line-height: 1.375rem;
                }
    
                p[data-test-selector="text"] {
                    display: inline-flex;
                     justify-content: flex-end;
                     padding-left: .625rem;
                     text-align: right;
                }
            }

            @media screen and (max-width: 576px) {
                > div {
                    .orderSummaryRowData {
                        > div {
                            width: 100%;
                        }
                    }
                }
            }
        }
        `,
    },
    pageTypeLinkGridItem: { 
        width: 12,
        css: css`
            background-color: #E5EEF7;
            width: 100%;
        `,
    },
    orderNumberGridItem: { 
        css: css`
            margin: 0 auto;
            
            @media screen and (min-width: 1280px) {
                min-width: 91px;
            }
            @media screen and (min-width: 991px) {
                min-width: 97px;
            }
            @media screen and (min-width: 927px) {
                margin-left: 12px !important;
            }
            @media screen and (max-width: 926px) {
                > div {
                     flex-direction: row;
                }
                width: calc(100% - .438rem);
                 max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 26px !important;
            }
            // @media screen and (min-width: 1200px) {
            //     margin-left: 26px !important;
            // }
            @media screen and (min-width: 927px) and (max-width: 1279px) {               
                min-width: 138px;
                padding: 2.5px 0;
            }
        `,
    },
    webOrderNumberGridItem: {
        css: css`
            margin: 0 auto;
            min-width: 105px;
            @media screen and (min-width: 927px) {
                margin-left: 18px !important;
            }
            @media screen and (max-width: 926px) {
                > div {
                     flex-direction: row;
                }
                width: calc(100% - .438rem);
                 max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 24px !important;
                min-width: 100px;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
            @media screen and (min-width: 927px) and (max-width: 1279px) {               
                min-width: 138px;
                padding: 2.5px 0;
            }
        `,
    },
    statusGridItem: { 
        css: css`
            margin: 0 auto;
            min-width: 5rem;
            @media screen and (min-width: 927px) {
                margin-left: 18px !important;
            }
            @media screen and (max-width: 926px) {
                > div {
                     flex-direction: row;
                }
                width: calc(100% - .438rem);
                 max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 18px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
            @media screen and (min-width: 927px) and (max-width: 1279px) {               
                min-width: 97px;
                padding: 2.5px 0;
            }
        `,
    },
    shipTo: { 
        css: css`
            margin: 0 auto;
            min-width: 7rem;
            text-transform: capitalize;
            @media screen and (min-width: 927px) {
                margin-left: 10px !important;
            }
            @media screen and (max-width: 926px) {
                > div {
                     flex-direction: row;
                }
                width: calc(100% - .438rem);
                 max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            // @media screen and (min-width: 1024px) {
            //     margin-left: 10px !important;
            // }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
            @media screen and (min-width: 927px) and (max-width: 1279px) {               
                min-width: 108px;
                padding: 2.5px 0;
            }
        `,
    },
    customerPo: { 
        css: css`
            margin: 0 auto;
            @media screen and (min-width: 927px) {
                margin-left: 10px !important;
            }
            @media screen and (max-width: 926px) {
                > div {
                     flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 14px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 37px !important;
            }
            @media screen and (min-width: 927px) and (max-width: 1279px) {               
                min-width: 87px;
                padding: 2.5px 0;
            }
        `,
    },
    channel: { 
        css: css`
            margin: 0 auto;
            @media screen and (min-width: 927px) {
                margin-left: 10px !important;
                min-width: 150px;
                padding: 2.5px 0;
            }
            @media screen and (max-width: 926px) {
                > div {
                     flex-direction: row;
                }
                width: calc(100% - .438rem);
                 max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem;
            }
            @media screen and (min-width: 1280px) {
                min-width: 114px;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 36px !important;
                min-width: 180px;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 38px !important;
                min-width: 180px;
            }
            @media screen and(min-width: 1279px) {
                min-width: 180px;
                padding: 2.5px 0;
            }
            @media screen and(min-width: 1024px) {
                min-width: 160px;
                padding: 2.5px 0;
            }
        `,
    },    
    totalGridItem: {
        css: css`
            margin: 0 auto;
            min-width: 7.5rem;

            p[data-test-selector="heading"] {
                color: #002554 !important;
            }
           
            p[data-test-selector="text"] {
                font-weight: bold !important;
                font-size: 1rem !important;
            }
            @media screen and (min-width: 1199px) {
                min-width: 15.625rem;
            }    

            @media screen and (min-width: 927px) {
                text-align: -webkit-right;
                justify-content: flex-end;
                margin-right: 0 !important;
                position: absolute;
                right: 20px;
                text-align: right;
            }
            @media screen and (max-width: 926px) {
                > div {
                     flex-direction: row;
                }
                width: calc(100% - .438rem);
                max-width: calc(100% - .438rem);
                flex-basis: calc(100% - .438rem);
                padding-left: 5.438rem !important;
                margin-top: .438rem !important;
            }

            @media screen and (min-width: 768px) and (max-width: 1024px) {
                font-size: .938rem !important;
            }
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
            @media screen and (max-width: 1279px) {
                min-width: 200px;
                padding: 2.5px 0;
            }
            @media screen and(max-width: 927px) {
                min-width: 200px;
            }
        `,
    },
    orderDateLabel: {
        css: css`
            color: #002554;
            font-family: 'Roboto', sans-serif;
            font-size: .875rem;
            letter-spacing: 1.31px;
            line-height: 1rem;
            display: inline-flex;
            margin-right: 1.125rem;
            vertical-align: middle;
            justify-content: center;
            align-items: center;
            letter-spacing: 0;
            line-height: 1.375rem;
        `,
    },
    orderDateValue: {
        css: css`
            color: #535E64 !important;
            font-family: 'Lato', sans-serif;
            font-size: 1rem;
            font-weight: bold;
            letter-spacing: .57px;
            line-height: 1.375rem;
            margin-left: 1.25rem;

            a > span {
                color: #535E64 !important;
                font-family: 'Lato', sans-serif;
                font-size: 1rem;
                font-weight: bold;
                letter-spacing: .57px;
                line-height: 1.375rem;
                margin-left: 1.25rem;
            }
        `,
    },
    orderDateWrapper: {
        css: css`
            color: #535E64;
            background-color: #E5EEF7;
            width: 100%;
            padding: .75rem .75rem .313rem 1.25rem;

            a > span {
                color: #002554 !important;
                font-family: 'Lato', sans-serif;
                font-size: 1rem;
                font-weight: bold;
                letter-spacing: .57px;
                line-height: 1.375rem;
            }
        `,
    },
    orderGrandTotalDisplay: {
        css: css`
            text-align: -webkit-right;
            justify-content: flex-end;
            align-items: end;
            margin-right: 0 !important;
            @media screen and (min-width: 1024px) {
                margin-left: 10px !important;
            }
            @media screen and (min-width: 1200px) {
                margin-left: 26px !important;
            }
        `,
    },
    orderView: {
        css: css`
            padding: 0 .938rem;
            position: relative;
            text-align: center;
            align-items: center;
            justify-content: center;

            &:after {
                content: '';
                display: block;
                height: 1.875rem;
                width: 1px;
                position: absolute;
                right: .938rem;
                border-right: 1px solid #E0E0E0;
            }

            @media screen and (min-width: 927px) {
                text-align: -webkit-left;
                justify-content: flex-start;
                margin-left: 0 !important;
                padding-left: 0 !important;
                min-width: 35px;
                max-width: 35px;
                align-items: flex-start;
                top: 5px;

                &:after {
                    right: auto;
                    left: 2.5rem;
                }
            }

            @media screen and (max-width: 926px) {
                width: 3.75rem;
                position: absolute;
                height: 100%;
                max-width: 3.75rem;
                flex-basis: 3.75rem;
                left: 1.563rem;
                top: 1.25rem;
                align-items: flex-start;
                padding-left: 0;
                justify-content: flex-start;

                &:after {
                    position: relative;
                    height: 85%;
                    left: 1.563rem;
                    top: -0.25rem;
                }
            }
        `,
    }
};

const OrderSummaryCard: FC<Props> = ({ language, order, orderSettings, extendedStyles,orderDetailsPageLink }) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));
    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    const orderNumber = order.erpOrderNumber || order.webOrderNumber;

    return ( 
        <GridItem {...styles.orderSummaryCardGridItem}>
            <Typography as="div" {...styles.orderDateWrapper} data-test-selector="orderSummaryCard_Date">
                    <Typography as="span" {...styles.orderDateLabel}>{translate("Order Date")}</Typography>
                    {orderNumber && <OrderDetailPageTypeLink {...styles.orderDateValue} title={orderDateDisplay} orderNumber={orderNumber} />}
            </Typography>
            <GridContainer {...styles.container} data-test-selector={`orderSummaryCard_${orderNumber}`}>
                <GridItem {...styles.orderView}>
                    <Link href={`${orderDetailsPageLink?.url}?orderNumber=${orderNumber}`}>
                        <Icon src="Eye" size={24} />
                    </Link>
                   
                </GridItem>
                <GridItem {...styles.orderNumberGridItem} className="orderSummaryRowData">
                    <SmallHeadingAndText
                        heading={translate("Order #")}
                        text={order.erpOrderNumber?order.erpOrderNumber:translate("Pending")}
                        extendedStyles={styles.orderNumberHeadingAndText}
                    />
                </GridItem>
                <GridItem {...styles.webOrderNumberGridItem} className="orderSummaryRowData">
                    <SmallHeadingAndText
                        heading={translate("Confirmation # ")}
                        text={order.webOrderNumber}
                        extendedStyles={styles.webOrderNumberHeadingAndText}
                    />
                </GridItem>
                <GridItem {...styles.statusGridItem} className="orderSummaryRowData">
                    {order.statusDisplay && (
                        <SmallHeadingAndText
                            heading={translate("Status")}
                            text={order.statusDisplay}
                            extendedStyles={styles.statusHeadingAndText}
                        />
                    )}
                </GridItem>

                <GridItem {...styles.shipTo} className="orderSummaryRowData">
                    {order.shipToCity && (
                        <SmallHeadingAndText
                            heading={translate("Ship to ")}
                            text={`${order.stCompanyName.toLowerCase()}`}
                            extendedStyles={styles.statusHeadingAndText}
                        />
                    )}
                </GridItem>

                <GridItem {...styles.customerPo} className="orderSummaryRowData">
                    {order.shipToCity && (
                        <SmallHeadingAndText
                            heading={translate("PO ")}
                            text={order.customerPO}
                            extendedStyles={styles.statusHeadingAndText}
                        />
                    )}
                </GridItem>

                <GridItem {...styles.channel} className="orderSummaryRowData">
                    {order.shipToCity && (
                        <SmallHeadingAndText
                            heading={translate("Channel ")}
                            text={order?.properties?.orderChannel}
                            extendedStyles={styles.statusHeadingAndText}
                        />
                    )}
                </GridItem>
                
                <GridItem {...styles.totalGridItem} className="orderSummaryRowData">
                    {order.orderGrandTotalDisplay && (
                        <SmallHeadingAndText
                            heading={translate("Total")}
                            text={order.orderGrandTotalDisplay}
                            extendedStyles={styles.totalHeadingAndText}
                        />
                    )}
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

export default connect(mapStateToProps)(OrderSummaryCard);
